import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const UserGoldPlans = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, planData } = location.state || {};
  const [currentIndex, setCurrentIndex] = useState(0);

  console.log("Gold Plans - Received data:", { user, planData }); // Debug log

  // Check if planData exists - handle both array and object structures
  let goldPlans = [];

  if (planData) {
    if (Array.isArray(planData)) {
      // If planData is already an array, use it directly
      goldPlans = planData;
    } else if (planData.plans && Array.isArray(planData.plans)) {
      // If planData has a plans array, use that
      goldPlans = planData.plans;
    } else if (typeof planData === 'object') {
      // If it's a single plan object, wrap it in an array
      goldPlans = [planData];
    }
  }

  console.log("Gold Plans - Processed goldPlans:", goldPlans); // Debug log

  // SIMPLIFIED: Remove filtering and show all plans that come through
  // If you want to filter only gold plans, you can add this back later
  const displayPlans = goldPlans;

  if (!displayPlans || !displayPlans.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Gold Plans Available</h3>
          <p className="text-gray-500 mb-6">We couldn't find any Gold Chit plans in your data.</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate(-1)}
              className="w-full px-6 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
            >
              Go Back
            </button>
            <button
              onClick={() => console.log("Debug data:", { location, user, planData, goldPlans })}
              className="w-full px-6 py-2 bg-gray-500 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
            >
              Debug Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  const goldPlan = displayPlans[currentIndex];

  // Map the data according to your JSON structure with safe defaults
  const {
    planName = "Gold Plan",
    planId = "N/A",
    chitTitle = planName,
    category = "Gold",
    goldRate = 0,
    goldGram = 0,
    monthlyInstallment = 0,
    numMonths = 0,
    totalPaid = 0,
    planStatus = "Active",
    location: planLocation = "N/A",
    installmentDetailsList = [],
  } = goldPlan;

  console.log("Gold Plans - Current plan data:", goldPlan); // Debug log

  // Calculate financial details based on available data
  const totalPlanCost = totalPaid || (monthlyInstallment && numMonths ? monthlyInstallment * numMonths : 0);
  
  const currentPaidAmount = installmentDetailsList && Array.isArray(installmentDetailsList)
    ? installmentDetailsList
        .filter((item) => item.installmentNumber !== "Total" && item.paidAmount)
        .reduce((sum, item) => sum + (item.installmentAmount || 0), 0)
    : 0;

  const remainingBalance = totalPlanCost - currentPaidAmount;

  // If no installment details, show appropriate message
  if (!installmentDetailsList || !Array.isArray(installmentDetailsList) || !installmentDetailsList.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Installment Details</h3>
          <p className="text-gray-500 mb-2">{chitTitle || planName}</p>
          <p className="text-gray-400 text-sm mb-6">No installment details available yet.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center">
            <div className="mb-6 lg:mb-0">
              <h1 className="text-3xl lg:text-4xl font-bold mb-2">
                {chitTitle || planName || `Gold Plan ${planId}`}
              </h1>
              <p className="text-teal-100 text-lg">Plan ID: {planId}</p>
              <p className="text-teal-100 text-sm mt-1">Category: {category}</p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="px-8 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl font-semibold hover:bg-white/30 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              ← Back to Profile
            </button>
          </div>
        </div>

        {/* Plan Navigation */}
        {displayPlans.length > 1 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <button
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex((i) => i - 1)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 mb-4 sm:mb-0 w-full sm:w-auto ${currentIndex === 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 shadow-md hover:shadow-lg"
                  }`}
              >
                ← Previous Plan
              </button>
              
              <div className="bg-gray-50 px-6 py-3 rounded-xl mb-4 sm:mb-0">
                <p className="text-gray-700 font-semibold text-center">
                  Plan <span className="text-2xl text-teal-600 mx-2">{currentIndex + 1}</span> of {displayPlans.length}
                </p>
              </div>
              
              <button
                disabled={currentIndex === displayPlans.length - 1}
                onClick={() => setCurrentIndex((i) => i + 1)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 w-full sm:w-auto ${currentIndex === displayPlans.length - 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-emerald-500 to-green-500 text-white hover:from-emerald-600 hover:to-green-600 shadow-md hover:shadow-lg"
                  }`}
              >
                Next Plan →
              </button>
            </div>
          </div>
        )}

        {/* Rest of your component remains the same */}
        {/* Summary Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {[
            { label: "Plan ID", value: planId, bg: "from-blue-50 to-cyan-50", text: "text-blue-700", icon: "📋" },
            { label: "Gold Grams", value: goldGram ? `${goldGram} g` : "N/A", bg: "from-amber-50 to-yellow-50", text: "text-amber-700", icon: "⚖️" },
            { label: "Gold Rate", value: goldRate ? `₹${goldRate}` : "N/A", bg: "from-purple-50 to-indigo-50", text: "text-purple-700", icon: "💎" },
            { label: "Monthly Installment", value: monthlyInstallment ? `₹${monthlyInstallment}` : "N/A", bg: "from-green-50 to-emerald-50", text: "text-green-700", icon: "💰" },
            { label: "Duration", value: numMonths ? `${numMonths} months` : "N/A", bg: "from-violet-50 to-fuchsia-50", text: "text-violet-700", icon: "⏱️" },
            { label: "Location", value: planLocation || "N/A", bg: "from-pink-50 to-rose-50", text: "text-pink-700", icon: "📍" },
          ].map((card, index) => (
            <div key={index} className={`bg-gradient-to-br ${card.bg} rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{card.label}</p>
                  <p className={`text-2xl font-bold ${card.text}`}>{card.value}</p>
                </div>
                <span className="text-3xl">{card.icon}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Financial Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          {[
            { 
              label: "Total Plan Cost", 
              value: totalPlanCost ? `₹${totalPlanCost}` : "N/A", 
              bg: "from-blue-100 to-cyan-100", 
              text: "text-blue-900",
              icon: "💳"
            },
            { 
              label: "Total Paid", 
              value: `₹${currentPaidAmount}`, 
              bg: "from-green-100 to-emerald-100", 
              text: "text-green-900",
              icon: "✅"
            },
            { 
              label: "Remaining Balance", 
              value: `₹${remainingBalance}`, 
              bg: "from-red-100 to-orange-100", 
              text: "text-red-900",
              icon: "⚡"
            },
            { 
              label: "Status", 
              value: planStatus || "Unknown", 
              bg: "from-teal-100 to-cyan-100", 
              text: "text-teal-900",
              icon: "📊"
            },
          ].map((card, index) => (
            <div key={index} className={`bg-gradient-to-br ${card.bg} rounded-2xl shadow-lg p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">{card.label}</p>
                  <p className={`text-2xl font-bold ${card.text}`}>{card.value}</p>
                </div>
                <span className="text-3xl opacity-80">{card.icon}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Installment Details */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-gray-800 to-slate-900 px-8 py-6">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Installment Details
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="py-4 px-6 text-left font-semibold text-gray-700"># Installment</th>
                  <th className="py-4 px-6 text-left font-semibold text-gray-700">Amount</th>
                  <th className="py-4 px-6 text-left font-semibold text-gray-700">Due Date</th>
                  <th className="py-4 px-6 text-left font-semibold text-gray-700">Penalty</th>
                  <th className="py-4 px-6 text-left font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {installmentDetailsList
                  .filter((item) => item.installmentNumber !== "Total")
                  .map((item, index) => (
                    <tr key={item.installmentNumber || index} className={`border-b hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="py-4 px-6 font-medium text-gray-900">
                        {item.installmentNumber || `#${index + 1}`}
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-semibold text-gray-800">
                          ₹{item.installmentAmount || "0"}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-600">
                        {item.dueDate || "-"}
                      </td>
                      <td className="py-4 px-6">
                        {item.penaltyAmount ? (
                          <span className="text-red-600 font-semibold">₹{item.penaltyAmount}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${item.paidAmount
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                          }`}>
                          {item.paidAmount ? (
                            <>
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Paid
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                              Pending
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Progress Visualization */}
        {totalPlanCost > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Payment Progress</h3>
              <span className="text-sm font-medium text-gray-600">
                {Math.round((currentPaidAmount / totalPlanCost) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className="bg-gradient-to-r from-green-400 to-emerald-600 h-4 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(currentPaidAmount / totalPlanCost) * 100}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600 mt-2">
              <span>₹0</span>
              <span>₹{totalPlanCost}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserGoldPlans;